import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Lock, Mail, LogIn } from 'lucide-react';
import FadeIn from '../components/ui/FadeIn';
import { useAuth } from '../context/AuthContext';
import { ApiError } from '../lib/api';
import logo from '../assets/logo.png';
import ThemeToggle from '../components/ui/ThemeToggle';
import { useConsoleTheme } from '../lib/theme';

const inputClasses =
  'w-full px-5 py-3.5 rounded-xl bg-white/[0.04] border border-white/[0.06] text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-[#B0FC38]/30 focus:border-[#B0FC38]/30 focus:bg-white/[0.06] transition-all duration-300 shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]';

// Sprint J — admin + ops land on /admin/dashboard (the command-center view);
// client lands on /portal/overview. /admin index also redirects to dashboard.
// MP.2 — media users land on /media (own-only calendar + dashboard).
function defaultLandingFor(user) {
  if (!user) return '/';
  if (user.role === 'nexsav_admin' || user.role === 'ops') return '/admin/dashboard';
  if (user.role === 'media') return '/media';
  return '/portal/overview';
}

export default function Login() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  useConsoleTheme(); // marks <html> with the console theme while mounted

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // ?next=/path — a same-origin path to return to (the assistant consent page
  // sends people here). Only relative paths are honoured.
  const nextParam = new URLSearchParams(location.search).get('next');
  const safeNext = nextParam && nextParam.startsWith('/') && !nextParam.startsWith('//') ? nextParam : null;

  useEffect(() => {
    if (!loading && user) {
      const target = location.state?.from || safeNext || defaultLandingFor(user);
      navigate(target, { replace: true });
    }
  }, [user, loading, location.state, safeNext, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    setSubmitting(true);
    try {
      const data = await login(email, password);
      const target = location.state?.from || safeNext || defaultLandingFor(data.user);
      navigate(target, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || 'Unable to sign in.');
      } else {
        setError('Network error, please try again.');
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="portal-skin min-h-screen relative flex flex-col">
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 grain-bg opacity-[0.13]" />
      </div>

      <div className="relative z-[1] flex-1 flex flex-col px-6 md:px-8 pt-10 pb-16">
        <FadeIn>
          <Link
            to="/"
            className="group inline-flex items-center gap-2 text-sm text-gray-500 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
            Back to nexsav.net
          </Link>
        </FadeIn>
        {/* Level with the back link, top right; absolute so nothing else moves. */}
        <div className="absolute top-[34px] right-6 md:right-8">
          <ThemeToggle />
        </div>

        <div className="flex-1 flex items-center justify-center">
          <FadeIn className="w-full max-w-md">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
              className="p-8 md:p-10 rounded-2xl bg-white/[0.025] backdrop-blur-sm border border-white/[0.06] shadow-[0_2px_20px_-4px_rgba(0,0,0,0.3)]"
            >
              <div className="flex flex-col items-center text-center mb-8">
                <img src={logo} alt="NexSav Innovate" data-light-invert className="h-10 w-auto mb-6 opacity-90" />
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/[0.08] mb-5">
                  <span className="text-[10px] font-medium text-[#B0FC38] tracking-wider uppercase">Client Portal</span>
                </div>
                <h1 className="text-2xl font-semibold text-white tracking-tight">
                  Welcome <span className="serif-acc accent-text">back</span>.
                </h1>
                <p className="text-sm text-gray-500 mt-2">Sign in to access your dashboard.</p>
              </div>

              <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
                <div>
                  <label htmlFor="email" className="block text-xs font-medium text-gray-400/80 mb-2 tracking-wide">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none" />
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      autoFocus
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@nexsav.net"
                      className={`${inputClasses} pl-11`}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-xs font-medium text-gray-400/80 mb-2 tracking-wide">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none" />
                    <input
                      id="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className={`${inputClasses} pl-11`}
                    />
                  </div>
                </div>

                {error && (
                  <div className="px-4 py-3 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-sm text-red-300/90">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="mt-2 inline-flex items-center justify-center gap-2 w-full px-6 py-3.5 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] shadow-lg shadow-[#B0FC38]/25 hover:shadow-[#B0FC38]/40 hover:scale-[1.01] active:scale-[0.99] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
                      Signing in…
                    </>
                  ) : (
                    <>
                      <LogIn className="w-4 h-4" />
                      Sign in
                    </>
                  )}
                </button>
              </form>

              <p className="mt-7 text-center text-[12px] text-gray-500 leading-relaxed">
                <Link
                  to="/forgot-password"
                  state={email ? { email } : undefined}
                  className="text-gray-300 hover:text-white underline underline-offset-4 decoration-white/20 hover:decoration-white/60 transition-colors"
                >
                  Forgot your password?
                </Link>
              </p>
            </motion.div>
          </FadeIn>
        </div>
      </div>
    </div>
  );
}
